import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useTeam } from '@/hooks/useTeam';
import { useAuth } from '@/contexts/AuthContext';
import { useCurrency } from '@/hooks/useCurrency';
import { Loader2 } from 'lucide-react';
import type { WonOpportunityData } from '@/hooks/useOpportunities';
import { useTranslation } from "react-i18next";
import {
  dateInputToIso,
  isFutureDateInputValue,
  todayDateInputValue,
} from '@/lib/dateInput';

interface WonOpportunityDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: WonOpportunityData) => void;
  opportunityTitle?: string;
  initialProduct?: string;
  initialPrice?: number;
}

export function WonOpportunityDialog({
  open,
  onClose,
  onConfirm,
  opportunityTitle,
  initialProduct,
  initialPrice
}: WonOpportunityDialogProps) {
  const { t } = useTranslation("crm");
  const { members, loading: loadingMembers } = useTeam();
  const { user } = useAuth();
  const { symbol } = useCurrency();
  const [product, setProduct] = useState('');
  const [price, setPrice] = useState('');
  const [saleDate, setSaleDate] = useState('');
  const [selectedMemberId, setSelectedMemberId] = useState<string>('');
  const [notes, setNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const activeMembers = members.filter(m => m.is_active);
  const currentMemberId =
    activeMembers.find((member) => member.auth_user_id === user?.auth_user_id)?.id ?? '';

  const today = todayDateInputValue();
  const saleDateIsFuture = isFutureDateInputValue(saleDate);

  useEffect(() => {
    if (open) {
      setProduct(initialProduct || '');
      setPrice(initialPrice ? String(initialPrice) : '');
      setSaleDate(todayDateInputValue());
      setNotes('');
    }
  }, [open, initialProduct, initialPrice]);

  // Defaulting to the logged-in member keeps the seller ranking populated, since no
  // field in this dialog is required to confirm
  useEffect(() => {
    if (open) {
      setSelectedMemberId((current) => current || currentMemberId);
    }
  }, [open, currentMemberId]);

  const handleConfirm = async () => {
    if (saleDateIsFuture) return;

    setIsSubmitting(true);

    const data: WonOpportunityData = {
      product: product.trim() || undefined,
      price: price ? parseFloat(price) : undefined,
      sale_date: dateInputToIso(saleDate),
      won_by_user_id: selectedMemberId || undefined,
      notes: notes.trim() || undefined
    };

    onConfirm(data);
    setIsSubmitting(false);
    handleClose();
  };

  const handleClose = () => {
    setProduct('');
    setPrice('');
    setSaleDate('');
    setSelectedMemberId('');
    setNotes('');
    onClose();
  };

  const formatCurrency = (value: string) => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';
    const number = parseInt(numericValue, 10) / 100;
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handlePriceChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value.replace(/\D/g, '');
    if (!rawValue) {
      setPrice('');
      return;
    }
    const numericValue = parseInt(rawValue, 10) / 100;
    setPrice(String(numericValue));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border rounded-2xl max-w-md dark:bg-[#18181B] dark:border-[#3F3F464D]">
        <DialogHeader>
          <DialogTitle className="text-foreground dark:text-white">{t("won.newTitle")}</DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {opportunityTitle && (
            <p className="text-sm text-muted-foreground dark:text-neutral-400">
              {t("won.newSubtitle")} <span className="text-foreground font-medium dark:text-white">{opportunityTitle}</span>
            </p>
          )}

          <div className="space-y-2">
            <Label className="text-foreground dark:text-white">{t("common.product")}</Label>
            <Input
              value={product}
              onChange={(e) => setProduct(e.target.value)}
              placeholder={t("common.productPlaceholder")}
              className="bg-white text-foreground border-border placeholder:text-muted-foreground dark:bg-neutral-600 dark:text-white dark:border-[#3F3F46] dark:placeholder:text-gray-400"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-foreground dark:text-white">Valor ({symbol})</Label>
            <Input
              value={price ? formatCurrency(String(Math.round(parseFloat(price) * 100))) : ''}
              onChange={handlePriceChange}
              placeholder="0,00"
              className="bg-white text-foreground border-border placeholder:text-muted-foreground dark:bg-neutral-600 dark:text-white dark:border-[#3F3F46] dark:placeholder:text-gray-400"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-foreground dark:text-white">{t("common.saleDate")}</Label>
            <Input
              type="date"
              value={saleDate}
              max={today}
              onChange={(e) => setSaleDate(e.target.value)}
              aria-invalid={saleDateIsFuture}
              className="bg-white text-foreground border-border dark:bg-neutral-600 dark:text-white dark:border-[#3F3F46] dark:[color-scheme:dark]"
            />
            {saleDateIsFuture && (
              <p className="text-sm text-red-500 dark:text-red-400">
                {t("common.saleDateFuture")}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <Label className="text-foreground dark:text-white">{t("common.teamMember")}</Label>
            <Select value={selectedMemberId} onValueChange={setSelectedMemberId} disabled={loadingMembers}>
              <SelectTrigger className="w-full bg-white text-foreground border-border dark:bg-neutral-600 dark:text-white dark:border-[#3F3F46]">
                <SelectValue placeholder={loadingMembers ? "Carregando..." : "Selecione um membro"} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {activeMembers.map((member) => (
                    <SelectItem key={member.id} value={member.id}>
                      {member.name || member.email}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-foreground dark:text-white">{t("common.note")}</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("common.notePlaceholder")}
              className="bg-white text-foreground border-border placeholder:text-muted-foreground min-h-[80px] resize-none dark:bg-neutral-600 dark:text-white dark:border-[#3F3F46] dark:placeholder:text-gray-400"
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            className="border-border text-foreground hover:bg-[#F1F5F9] dark:border-white dark:text-white dark:hover:bg-white dark:hover:text-black"
          >
            {t("common.cancel")}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={isSubmitting || saleDateIsFuture}
            className="bg-green-600 hover:bg-green-700"
          >
            {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Confirmar Venda'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
